// frontend/packages/business-console/src/pages/InboxReplySettings.jsx
//
// The tenant's voice + rules for the Inbox AI draft-reply (Phase 2). Reads and
// writes Business.settings.inboxReplyPolicy via GET/PUT /v2/inbox/reply-policy
// (businessadmin-only server-side; like the sibling settings pages we render
// for everyone and surface a 403 on save). The deterministic house guardrails
// (no em-dashes, no AI-slop, never invent facts, never auto-send) always apply
// and are NOT configurable here; this page only shapes voice + what the AI may
// or may not say.

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';

import { getReplyPolicy, updateReplyPolicy } from '../services/inboxService';
import { useTokens, PageHeader, Surface } from '../components/primitives';

const TONE_OPTIONS = [
  { value: 'friendly', label: 'Friendly' },
  { value: 'professional', label: 'Professional' },
  { value: 'direct', label: 'Direct' },
  { value: 'custom', label: 'Custom' },
];

// Lists are edited as one-item-per-line text; converted to arrays on save.
const linesToList = (text) =>
  String(text || '')
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
const listToLines = (list) => (Array.isArray(list) ? list.join('\n') : '');

export default function InboxReplySettings() {
  const t = useTokens();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedMsg, setSavedMsg] = useState(null);

  const [form, setForm] = useState({
    tonePreset: 'friendly',
    toneNotes: '',
    instructions: '',
    canSay: '', // textarea (lines)
    cantSay: '', // textarea (lines)
    signature: '',
  });

  useEffect(() => {
    let alive = true;
    getReplyPolicy()
      .then((p) => {
        if (!alive) return;
        setForm({
          tonePreset: p.tonePreset || 'friendly',
          toneNotes: p.toneNotes || '',
          instructions: p.instructions || '',
          canSay: listToLines(p.canSay),
          cantSay: listToLines(p.cantSay),
          signature: p.signature || '',
        });
      })
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await updateReplyPolicy({
        tonePreset: form.tonePreset,
        toneNotes: form.toneNotes,
        instructions: form.instructions,
        canSay: linesToList(form.canSay),
        cantSay: linesToList(form.cantSay),
        signature: form.signature,
      });
      setSavedMsg('Reply style saved.');
    } catch (err) {
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.message;
      setError(
        status === 403
          ? serverMsg || 'Only business admins can change the reply style.'
          : serverMsg || 'Could not save. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 880, mx: 'auto', width: '100%' }}>
      <PageHeader title="Reply style" sx={{ mb: 0.75 }} />
      <Typography sx={{ fontSize: 13.5, color: t.muted, mb: 3 }}>
        Teach the AI how you talk to your customers. When you click {'"Draft'}
        {' with AI"'} on a conversation, it writes in this voice and follows
        these rules. You always read and edit before sending, and nothing is
        ever sent automatically.
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      <Surface sx={{ p: 2.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <ChatBubbleOutlineOutlinedIcon
            fontSize="small"
            sx={{ color: 'text.secondary', mt: 0.5 }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
              Tone
            </Typography>
            <ToggleButtonGroup
              value={form.tonePreset}
              exclusive
              onChange={(_e, v) =>
                v && setForm((f) => ({ ...f, tonePreset: v }))
              }
              size="small"
              sx={{ flexWrap: 'wrap' }}
            >
              {TONE_OPTIONS.map((o) => (
                <ToggleButton key={o.value} value={o.value} sx={{ textTransform: 'none' }}>
                  {o.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <TextField
              label="Voice notes (optional)"
              placeholder="Warm and casual, use first names, keep it short."
              value={form.toneNotes}
              onChange={set('toneNotes')}
              multiline
              minRows={2}
              fullWidth
              sx={{ mt: 2 }}
              inputProps={{ maxLength: 600 }}
            />
          </Box>
        </Stack>

        <Divider sx={{ my: 2.5 }} />

        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
          How you reply
        </Typography>
        <TextField
          placeholder="Always thank them for reaching out, answer the question, and offer to hop on a quick call."
          value={form.instructions}
          onChange={set('instructions')}
          multiline
          minRows={3}
          fullWidth
          inputProps={{ maxLength: 1500 }}
        />

        <Divider sx={{ my: 2.5 }} />

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                It can say
              </Typography>
              <Chip size="small" label="one per line" variant="outlined" />
            </Stack>
            <TextField
              placeholder={'Free estimates\nOpen Sat 9-1\nWe are licensed and insured'}
              value={form.canSay}
              onChange={set('canSay')}
              multiline
              minRows={4}
              fullWidth
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                It must never say
              </Typography>
              <Chip size="small" label="one per line" variant="outlined" />
            </Stack>
            <TextField
              placeholder={'Never quote a price\nNo same-day promises\nNever mention competitors'}
              value={form.cantSay}
              onChange={set('cantSay')}
              multiline
              minRows={4}
              fullWidth
            />
          </Box>
        </Stack>

        <Divider sx={{ my: 2.5 }} />

        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
          Signature
        </Typography>
        <TextField
          placeholder={'Thanks,\nMike\nAce Plumbing'}
          value={form.signature}
          onChange={set('signature')}
          multiline
          minRows={2}
          fullWidth
          inputProps={{ maxLength: 400 }}
          helperText="Added to the end of every draft."
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2.5 }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {saving ? 'Saving…' : 'Save reply style'}
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          The AI always avoids robotic, AI-sounding phrasing, never uses
          em-dashes, and never makes up prices or facts, regardless of these
          settings. Only admins can change the reply style.
        </Typography>
      </Surface>

      <Snackbar
        open={Boolean(savedMsg)}
        autoHideDuration={3000}
        onClose={() => setSavedMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" onClose={() => setSavedMsg(null)}>
          {savedMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
